import { useEffect, useState } from 'react';

import { Tab, Tabs } from '@mui/material';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import TabPanel from './TabPanel';
import { assistantApis } from '../../../apis/assistantApis';
import { requestAll } from '../../../apis/config';
import { customInstanceApis } from '../../../apis/customInstanceApis';
import { modelApis } from '../../../apis/modelApis';
import { serverApis } from '../../../apis/serverApis';
import { solrApis } from '../../../apis/solrApis';
import { handleErrors } from '../../../helpers';
import useToast from '../../../hooks/useToast';
import {
  isNonEmptyArrayValue,
  mapSolrStatusToInstance,
  sentenceCase,
} from '../../../utils/utils';
import AlertMessage from '../../elements/alert-message/AlertMessage';
import Loading from '../../elements/loading/Loading';
import List from '../../sections/list/List';
import NoContent from '../../sections/no-content/NoContent';

const InstanceTabs = ({ projectId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [tab, setTab] = useState(0);

  const [solr, setSolr] = useState(null);
  const [gps, setGps] = useState(null);
  const [models, setModels] = useState(null);
  const [custom, setCustom] = useState(null);
  const [assistant, setAssistant] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (event, newValue) => setTab(newValue);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(false);
      try {
        const [
          instancesRes,
          gpsRes,
          modelRes,
          customInstanceRes,
          assistantRes,
        ] = await requestAll([
          solrApis.getInstances(projectId),
          serverApis.getServers(projectId),
          modelApis.getModels(projectId),
          customInstanceApis.getCustomInstances(projectId),
          assistantApis.getAssistantInstanceList(projectId),
        ]);

        const instancesWithSolrRes = await Promise.all(
          instancesRes.data.content.map(async (instance) => {
            try {
              const { data: solrStatus } = await solrApis.getSolrStatus(
                projectId,
                instance.id
              );
              const solrWithStatus = mapSolrStatusToInstance(
                instance,
                solrStatus
              );
              return solrWithStatus;
            } catch (error) {
              return { ...instance, solrRunning: [] };
            }
          })
        );

        setSolr(instancesWithSolrRes);
        setGps(gpsRes.data.content);
        setModels(modelRes.data.content);
        setCustom(customInstanceRes.data.content);
        setAssistant(assistantRes.data.content);
      } catch (error) {
        handleErrors(navigate, error, showToast);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <Loading />;

  if (error)
    return (
      <AlertMessage
        severity="error"
        message={sentenceCase(
          t('loading', {
            ns: 'errors',
            content: t('projectInfo', { ns: 'project' }),
          })
        )}
      />
    );

  const instanceTabData = [
    { label: 'Solr', index: 0, data: solr, listTypeProp: 'isInstanceList' },
    {
      label: t('generalServer', { ns: 'project' }),
      index: 1,
      data: gps,
      listTypeProp: 'isGpsList',
    },
    {
      label: t('models', { ns: 'model' }),
      index: 2,
      data: models,
      listTypeProp: 'isModelList',
    },
    {
      label: t('assistant', { ns: 'assistant' }),
      index: 4,
      data: assistant,
      listTypeProp: 'isAssistantList',
    },
    {
      label: t('customInst', { ns: 'titles' }),
      index: 3,
      data: custom,
      listTypeProp: 'isCustomInstList',
    },
  ];

  const instanceList = instanceTabData.filter((instanceGroup) =>
    isNonEmptyArrayValue(instanceGroup.data)
  );

  if (instanceList.length === 0) {
    return <NoContent title={t('noInstances', { ns: 'instance' })} />;
  } else {
    return (
      <Box>
        <Tabs value={tab} onChange={handleChange}>
          {instanceList.map((instanceGroup) => (
            <Tab label={instanceGroup.label} key={instanceGroup.index} />
          ))}
        </Tabs>
        {instanceList.map((instanceGroup, index) => (
          <TabPanel value={tab} index={index} key={index}>
            <List
              data={instanceGroup.data}
              // dynamically set list type prop
              {...{ [instanceGroup.listTypeProp]: true }}
            />
          </TabPanel>
        ))}
      </Box>
    );
  }
};

InstanceTabs.propTypes = {
  projectId: PropTypes.string.isRequired,
};

export default InstanceTabs;
